import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormDialog } from 'components';
import { ENTERPRISE_EQUITY_OPTIONS } from 'pages/ValuationsAllocation/common/constants/valuations';
import { ValuationsFormState } from 'pages/ValuationsAllocation/types';
import { getNumberValue, getStringValue, gridShortDate } from 'utilities';
import { ApproachForm } from './components';
import { ApproachDialogProps } from './types';
import { VALUATION_APPROACHES_TEST_IDS } from '../../ValuationApproaches.testIds';

const FORM_DEFAULT_VALUES = Object.freeze({
  isValid: true,
  values: {},
  errors: {},
} as ValuationsFormState);

const ApproachDialog: FC<ApproachDialogProps> = props => {
  const {
    approach,
    calibration,
    rangeSensitivity,
    editMode,
    handleOnClose,
    handleOnSave,
    measurementDate,
    open,
    shouldDisplayGPCOptions,
    isEquityOptionAvailable,
    title,
  } = props;

  const [formState, setFormState] = useState<ValuationsFormState>(FORM_DEFAULT_VALUES);

  const formIsValid = useMemo(() => Boolean(formState.isValid), [formState]);

  const handleOnClick = useCallback(() => handleOnSave(formState), [formState, handleOnSave]);

  // Set Form values from Approach
  useEffect(() => {
    setFormState(prevState => ({
      ...prevState,
      values: {
        ...prevState.values,
        name: approach ? getStringValue(approach?.name) : (calibration?.name ?? rangeSensitivity?.name),
        valuation_date: gridShortDate(approach?.valuation_date ?? measurementDate),
        financial_statement_id: getNumberValue(approach?.financial_statement),
        is_calibration: approach?.is_calibration,
        enterprise_equity_value: approach?.valuations_approach_gpc?.is_evaluating_equity_value
          ? ENTERPRISE_EQUITY_OPTIONS[1]
          : ENTERPRISE_EQUITY_OPTIONS[0],
        use_multiple_premium_discount: approach?.valuations_approach_gpc?.use_multiple_premium_discount,
        use_forward_multiple_discount: approach?.valuations_approach_gpc?.use_forward_multiple_discount,
      },
    }));
  }, [approach, calibration, rangeSensitivity, measurementDate]);

  const handleOnChange = (updatedFormState: ValuationsFormState) => setFormState(updatedFormState);

  return (
    <FormDialog
      editMode={editMode}
      isValid={formIsValid}
      onClose={handleOnClose}
      onSave={handleOnClick}
      open={open}
      saveButtonTestId={VALUATION_APPROACHES_TEST_IDS.approachDialogSaveButton}
      PaperProps={{
        style: {
          maxWidth: '27.5rem',
        },
      }}
      title={title}>
      <ApproachForm
        formState={formState}
        onFormChange={handleOnChange}
        shouldDisplayGPCOptions={shouldDisplayGPCOptions}
        isEquityOptionAvailable={isEquityOptionAvailable}
      />
    </FormDialog>
  );
};

export default ApproachDialog;
